import React from 'react';
import { motion } from 'framer-motion';
import { Shield, Eye, Lock, Database, Mail, Phone, MapPin } from 'lucide-react';
import Navbar from '../components/Navbar';
import SEOHead from '../components/SEOHead';
import Footer from '../components/Footer';

const PrivacyPolicyPage = () => {
  return (
    <div className="min-h-screen bg-white">
      <SEOHead 
        title="Privacy Policy"
        description="How CTC Waterworks collects, uses, and protects your personal information."
        canonicalPath="/privacy-policy"
        noIndex={true}
      />
      <Navbar />
      
      {/* Hero Section */}
      <section className="pt-24 pb-16 bg-gradient-to-br from-slate-50 via-ctc-silver-light to-white">
        <div className="max-w-4xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <div className="inline-flex items-center space-x-3 bg-ctc-blue/10 text-ctc-blue px-4 py-2 rounded-full text-sm font-medium mb-6">
              <Shield size={16} />
              <span>Your Privacy Matters</span>
            </div>
            <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              Privacy Policy
            </h1>
            <p className="text-xl text-gray-600 mb-8">
              How CTC Waterworks collects, uses, and protects your personal information
            </p>
            <div className="text-sm text-gray-500">
              Last updated: December 2024
            </div>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-6">
          <div className="prose prose-lg max-w-none">
            
            {/* Introduction */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="mb-12"
            >
              <div className="flex items-center space-x-3 mb-6">
                <div className="w-10 h-10 bg-gradient-to-br from-ctc-blue to-ctc-blue-dark rounded-lg flex items-center justify-center">
                  <Eye className="text-white" size={20} />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 m-0">Introduction</h2>
              </div>
              <div className="bg-gradient-to-br from-ctc-blue/5 to-ctc-blue/10 rounded-xl p-6 border border-ctc-blue/20">
                <p className="text-gray-700 leading-relaxed m-0">
                  CTC Waterworks ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website, use our services, or interact with us. Please read this privacy policy carefully. If you do not agree with the terms of this privacy policy, please do not access the site or use our services.
                </p>
              </div>
            </motion.div>

            {/* Information We Collect */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="mb-12"
            >
              <div className="flex items-center space-x-3 mb-6">
                <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-emerald-500 rounded-lg flex items-center justify-center">
                  <Database className="text-white" size={20} />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 m-0">Information We Collect</h2>
              </div>
              
              <div className="space-y-6">
                <div className="bg-white rounded-lg p-6 border border-gray-200 shadow-sm">
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">Personal Information</h3>
                  <p className="text-gray-700 mb-4">We may collect personal information that you voluntarily provide to us when you:</p>
                  <ul className="list-disc list-inside space-y-2 text-gray-700">
                    <li>Fill out contact forms or request quotes</li>
                    <li>Subscribe to our newsletter or marketing communications</li>
                    <li>Register for events or webinars</li>
                    <li>Contact us via phone, email, or other communication methods</li>
                    <li>Apply for employment opportunities</li>
                  </ul>
                  <p className="text-gray-700 mt-4">This information may include:</p>
                  <ul className="list-disc list-inside space-y-2 text-gray-700">
                    <li>Name and contact information (email, phone, address)</li>
                    <li>Company name and job title</li>
                    <li>Agricultural operation details (crop types, acreage, etc.)</li>
                    <li>Communication preferences</li>
                  </ul>
                </div>

                <div className="bg-white rounded-lg p-6 border border-gray-200 shadow-sm">
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">Automatically Collected Information</h3>
                  <p className="text-gray-700 mb-4">When you visit our website, we may automatically collect certain information about your device and usage patterns:</p>
                  <ul className="list-disc list-inside space-y-2 text-gray-700">
                    <li>IP address and location data</li>
                    <li>Browser type and version</li>
                    <li>Operating system</li>
                    <li>Pages visited and time spent on our site</li>
                    <li>Referring website information</li>
                    <li>Device identifiers and characteristics</li>
                  </ul>
                </div>
              </div>
            </motion.div>

            {/* How We Use Information */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="mb-12"
            >
              <div className="flex items-center space-x-3 mb-6">
                <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-indigo-500 rounded-lg flex items-center justify-center">
                  <Lock className="text-white" size={20} />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 m-0">How We Use Your Information</h2>
              </div>
              
              <div className="bg-white rounded-lg p-6 border border-gray-200 shadow-sm">
                <p className="text-gray-700 mb-4">We use the information we collect for various business purposes, including:</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-3">Service Delivery</h4>
                    <ul className="list-disc list-inside space-y-1 text-gray-700 text-sm">
                      <li>Responding to inquiries and quote requests</li>
                      <li>Providing technical support and customer service</li>
                      <li>Processing orders and managing accounts</li>
                      <li>Scheduling installations and maintenance</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-3">Communication</h4>
                    <ul className="list-disc list-inside space-y-1 text-gray-700 text-sm">
                      <li>Sending product updates and newsletters</li>
                      <li>Notifying about service appointments</li>
                      <li>Sharing industry news and educational content</li>
                      <li>Marketing relevant products and services</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-3">Business Operations</h4>
                    <ul className="list-disc list-inside space-y-1 text-gray-700 text-sm">
                      <li>Improving our website and services</li>
                      <li>Analyzing usage patterns and preferences</li>
                      <li>Conducting market research</li>
                      <li>Ensuring security and preventing fraud</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-3">Legal Compliance</h4>
                    <ul className="list-disc list-inside space-y-1 text-gray-700 text-sm">
                      <li>Meeting regulatory requirements</li>
                      <li>Responding to legal requests</li>
                      <li>Protecting our rights and interests</li>
                      <li>Enforcing our terms of service</li>
                    </ul>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Information Sharing */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="mb-12"
            >
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Information Sharing and Disclosure</h2>
              
              <div className="bg-white rounded-lg p-6 border border-gray-200 shadow-sm">
                <p className="text-gray-700 mb-4">We do not sell, trade, or rent your personal information to third parties. We may share your information in the following limited circumstances:</p>
                <div className="space-y-4">
                  <div className="border-l-4 border-ctc-blue pl-4">
                    <h4 className="font-semibold text-gray-900 mb-2">Service Providers</h4>
                    <p className="text-gray-700 text-sm">We may share information with trusted third-party service providers who assist us in operating our business, such as website hosting, email services, and customer relationship management platforms.</p>
                  </div>
                  <div className="border-l-4 border-green-500 pl-4">
                    <h4 className="font-semibold text-gray-900 mb-2">Authorized Dealers</h4>
                    <p className="text-gray-700 text-sm">With your consent, we may share your contact information with our authorized dealers in your area to provide local service and support.</p>
                  </div>
                  <div className="border-l-4 border-orange-500 pl-4">
                    <h4 className="font-semibold text-gray-900 mb-2">Legal Requirements</h4>
                    <p className="text-gray-700 text-sm">We may disclose information when required by law, court order, or government regulation, or to protect our rights, property, or safety.</p>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Data Security */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="mb-12"
            >
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Data Security</h2>
              
              <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-6 border border-green-200">
                <p className="text-gray-700 mb-4">We implement appropriate technical and organizational security measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. These measures include:</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <ul className="list-disc list-inside space-y-2 text-gray-700">
                    <li>Secure Socket Layer (SSL) encryption</li>
                    <li>Regular security assessments</li>
                    <li>Access controls and authentication</li>
                    <li>Employee training on data protection</li>
                  </ul>
                  <ul className="list-disc list-inside space-y-2 text-gray-700">
                    <li>Secure data storage and backup systems</li>
                    <li>Network security monitoring</li>
                    <li>Incident response procedures</li>
                    <li>Regular software updates and patches</li>
                  </ul>
                </div>
              </div>
            </motion.div>

            {/* Your Rights */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="mb-12"
            >
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Your Privacy Rights</h2>
              
              <div className="bg-white rounded-lg p-6 border border-gray-200 shadow-sm">
                <p className="text-gray-700 mb-4">Depending on your location, you may have certain rights regarding your personal information:</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-3">Access and Portability</h4>
                    <ul className="list-disc list-inside space-y-1 text-gray-700 text-sm">
                      <li>Request access to your personal information</li>
                      <li>Receive a copy of your data in a portable format</li>
                      <li>Request information about how we use your data</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-3">Correction and Deletion</h4>
                    <ul className="list-disc list-inside space-y-1 text-gray-700 text-sm">
                      <li>Correct inaccurate or incomplete information</li>
                      <li>Request deletion of your personal information</li>
                      <li>Withdraw consent for data processing</li>
                    </ul>
                  </div>
                </div>
                <div className="mt-6 p-4 bg-ctc-blue/5 rounded-lg border border-ctc-blue/20">
                  <p className="text-gray-700 text-sm">
                    To exercise these rights, please contact us using the information provided below. We will respond to your request within a reasonable timeframe and in accordance with applicable law.
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Cookies and Tracking */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="mb-12"
            >
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Cookies and Tracking Technologies</h2>
              
              <div className="bg-white rounded-lg p-6 border border-gray-200 shadow-sm">
                <p className="text-gray-700 mb-4">We use cookies and similar tracking technologies to enhance your browsing experience and analyze website usage. Types of cookies we use include:</p>
                <div className="space-y-4">
                  <div className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-ctc-blue rounded-full mt-2 flex-shrink-0"></div>
                    <div>
                      <h4 className="font-semibold text-gray-900">Essential Cookies</h4>
                      <p className="text-gray-700 text-sm">Required for basic website functionality and security</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0"></div>
                    <div>
                      <h4 className="font-semibold text-gray-900">Analytics Cookies</h4>
                      <p className="text-gray-700 text-sm">Help us understand how visitors interact with our website</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-orange-500 rounded-full mt-2 flex-shrink-0"></div>
                    <div>
                      <h4 className="font-semibold text-gray-900">Marketing Cookies</h4>
                      <p className="text-gray-700 text-sm">Used to deliver relevant advertisements and track campaign effectiveness</p>
                    </div>
                  </div>
                </div>
                <p className="text-gray-700 text-sm mt-4">
                  You can control cookie settings through your browser preferences. However, disabling certain cookies may affect website functionality.
                </p>
              </div>
            </motion.div>

            {/* Contact Information */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="mb-12"
            >
              <div className="flex items-center space-x-3 mb-6">
                <div className="w-10 h-10 bg-gradient-to-br from-ctc-blue to-ctc-blue-dark rounded-lg flex items-center justify-center">
                  <Mail className="text-white" size={20} />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 m-0">Contact Us</h2>
              </div>
              
              <div className="bg-gradient-to-br from-ctc-blue/5 to-ctc-blue/10 rounded-xl p-6 border border-ctc-blue/20">
                <p className="text-gray-700 mb-4">
                  If you have questions about this Privacy Policy or our data practices, please contact us:
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-3">CTC Waterworks</h4>
                    <div className="space-y-2 text-gray-700">
                      <div className="flex items-center space-x-2">
                        <MapPin size={16} className="text-ctc-blue" />
                        <span>460 McLaughlin Rd, Yakima, WA 98908</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Phone size={16} className="text-ctc-blue" />
                        <span>(509) 901-7476</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Mail size={16} className="text-ctc-blue" />
                        <span>privacy@ctcwaterworks.com</span>
                      </div>
                    </div>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-3">Privacy Officer</h4>
                    <p className="text-gray-700 text-sm">
                      For specific privacy-related inquiries, you may contact our Privacy Officer directly at the email address above. We will respond to your inquiry within 30 days.
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Updates */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Policy Updates</h2>
              
              <div className="bg-yellow-50 rounded-lg p-6 border border-yellow-200">
                <p className="text-gray-700">
                  We may update this Privacy Policy from time to time to reflect changes in our practices or applicable law. We will notify you of any material changes by posting the updated policy on our website and updating the "Last updated" date. Your continued use of our services after any changes indicates your acceptance of the updated policy.
                </p>
              </div>
            </motion.div>

          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PrivacyPolicyPage;